import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

const ThemeContext = createContext(null);

// Zone color palette - brand primary #ba232e
// MUI cannot parse var(...) - it needs a real colour to derive shades and
// contrast text from. The brand block is inlined in <head> before this module
// runs, so the computed value is already correct on first render.
const brandColor = (name, fallback) => {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback;
  try {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue(name).trim();
    return /^#[0-9a-fA-F]{3,8}$/.test(v) ? v : fallback;
  } catch {
    return fallback;
  }
};

const zoneColors = {
  // Core blacks
  black: '#000000',
  blackCard: '#0a0a0a',
  blackElevated: '#141414',
  blackMuted: '#1a1a1a',
  
  // Light mode equivalents
  white: '#ffffff',
  whitePage: '#f5f5f5',
  whiteMuted: '#fafafa',
  
  // Text Colors
  textWhite: '#ffffff',
  textLight: '#a3a3a3',
  textMuted: '#737373',
  textDark: '#0a0a0a',
  textDarkSecondary: '#525252',
  
  // Borders
  borderDark: '#262626',
  borderLight: '#e5e5e5',
  borderMuted: '#404040',
  
  // Accent - Brand primary (red)
  accent: brandColor('--brand-primary', '#ba232e'),
  accentHover: brandColor('--brand-primary-light', '#d42a37'),
  accentMuted: brandColor('--brand-primary-dark', '#951c26'),
  
  // Status colors (muted versions for dark theme)
  statusGreen: '#22c55e',
  statusRed: '#ef4444',
  statusBlue: '#3b82f6',
  statusYellow: '#eab308',
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem('themeMode');
    return stored || 'dark'; // Dark mode default
  });

  const toggleColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  const isDark = mode === 'dark';

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: zoneColors.accent,
        light: zoneColors.accentHover,
        dark: zoneColors.accentMuted,
        contrastText: '#ffffff'
      },
      secondary: {
        main: isDark ? zoneColors.blackElevated : zoneColors.whiteMuted,
        light: isDark ? zoneColors.borderMuted : zoneColors.borderLight,
        dark: isDark ? zoneColors.black : zoneColors.whitePage,
        contrastText: isDark ? zoneColors.textWhite : zoneColors.textDark
      },
      error: {
        main: zoneColors.statusRed
      },
      success: {
        main: zoneColors.statusGreen
      },
      warning: {
        main: zoneColors.statusYellow
      },
      info: {
        main: zoneColors.statusBlue
      },
      background: {
        default: isDark ? zoneColors.black : zoneColors.whitePage,
        paper: isDark ? zoneColors.blackCard : zoneColors.white
      },
      text: {
        primary: isDark ? zoneColors.textWhite : zoneColors.textDark,
        secondary: isDark ? zoneColors.textMuted : zoneColors.textDarkSecondary
      },
      divider: isDark ? zoneColors.borderDark : zoneColors.borderLight
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 13,
      h1: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 700, 
        fontSize: '1.75rem', 
        letterSpacing: '-0.02em',
        textTransform: 'uppercase'
      },
      h2: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600, 
        fontSize: '1.5rem', 
        letterSpacing: '-0.01em' 
      },
      h3: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600, 
        fontSize: '1.25rem' 
      },
      h4: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600, 
        fontSize: '1.1rem' 
      },
      h5: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600, 
        fontSize: '1rem' 
      },
      h6: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontWeight: 600, 
        fontSize: '0.8125rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      },
      body1: { fontSize: '0.8125rem' },
      body2: { fontSize: '0.75rem' },
      caption: { 
        fontSize: '0.6875rem',
        fontFamily: '"JetBrains Mono", monospace',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      },
      button: { 
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.75rem', 
        fontWeight: 600, 
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      },
      overline: {
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: '0.625rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.1em'
      }
    },
    shape: {
      borderRadius: 0
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Avoid horizontal layout shift when overflow toggles (e.g. tooltip poppers,
          // hover effects) — scrollbar column stays reserved.
          html: {
            scrollbarGutter: 'stable'
          },
          body: {
            backgroundColor: isDark ? zoneColors.black : zoneColors.whitePage,
            scrollbarWidth: 'thin',
            scrollbarColor: isDark 
              ? `${zoneColors.borderMuted} ${zoneColors.black}` 
              : `${zoneColors.borderLight} ${zoneColors.whitePage}`
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            padding: '6px 12px',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            minHeight: 32
          },
          containedPrimary: {
            backgroundColor: zoneColors.accent,
            color: '#ffffff',
            border: `1px solid ${zoneColors.accent}`,
            '&:hover': {
              backgroundColor: zoneColors.accentHover,
              borderColor: zoneColors.accentHover
            }
          },
          containedSecondary: {
            backgroundColor: isDark ? zoneColors.borderDark : zoneColors.borderLight,
            color: isDark ? zoneColors.textWhite : zoneColors.textDark,
            border: `1px solid ${isDark ? zoneColors.borderMuted : zoneColors.borderLight}`,
            '&:hover': {
              backgroundColor: isDark ? zoneColors.borderMuted : '#e5e5e5',
              borderColor: isDark ? zoneColors.textMuted : zoneColors.textDarkSecondary
            }
          },
          outlined: {
            borderColor: isDark ? zoneColors.borderMuted : zoneColors.borderLight,
            color: isDark ? zoneColors.textWhite : zoneColors.textDark,
            '&:hover': {
              borderColor: zoneColors.accent,
              backgroundColor: 'transparent'
            }
          },
          text: {
            color: isDark ? zoneColors.textLight : zoneColors.textDarkSecondary,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              color: zoneColors.accent
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            border: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`,
            boxShadow: 'none',
            backgroundColor: isDark ? zoneColors.blackCard : zoneColors.white
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 0
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? zoneColors.blackElevated : zoneColors.whiteMuted,
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 600,
              fontSize: '0.625rem',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              color: isDark ? zoneColors.textLight : zoneColors.textDarkSecondary,
              borderBottom: `1px solid ${isDark ? zoneColors.borderMuted : zoneColors.borderLight}`,
              padding: '8px'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            padding: '6px 8px',
            borderBottom: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(186,35,46,0.03)' : 'rgba(186,35,46,0.02)'
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: '0.5625rem',
            fontWeight: 600,
            fontFamily: '"JetBrains Mono", monospace',
            borderRadius: 0,
            height: 20,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          },
          sizeSmall: {
            height: 18,
            fontSize: '0.5rem'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem'
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? zoneColors.borderDark : zoneColors.borderLight
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? zoneColors.borderMuted : zoneColors.textDarkSecondary
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: zoneColors.accent,
              borderWidth: 1
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            '&.Mui-focused': {
              color: zoneColors.accent
            }
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: zoneColors.black,
            borderBottom: `1px solid ${zoneColors.borderDark}`,
            boxShadow: 'none'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? zoneColors.black : zoneColors.white,
            borderRight: 'none'
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            marginBottom: 0,
            padding: '6px 12px',
            borderLeft: '2px solid transparent',
            '&.Mui-selected': {
              backgroundColor: 'transparent',
              borderLeftColor: zoneColors.accent,
              '& .MuiListItemIcon-root': { color: zoneColors.accent },
              '& .MuiListItemText-primary': { color: zoneColors.accent },
              '&:hover': {
                backgroundColor: isDark ? 'rgba(186,35,46,0.05)' : 'rgba(186,35,46,0.08)'
              }
            },
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)'
            }
          }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
            color: isDark ? zoneColors.textMuted : zoneColors.textDarkSecondary
          }
        }
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? zoneColors.textLight : zoneColors.textDark
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? zoneColors.borderDark : zoneColors.borderLight
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: '"JetBrains Mono", monospace',
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '0.6875rem',
            letterSpacing: '0.05em',
            minHeight: 32,
            padding: '6px 12px',
            '&.Mui-selected': {
              color: zoneColors.accent
            }
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 32,
            backgroundColor: isDark ? zoneColors.blackElevated : zoneColors.whiteMuted,
            borderRadius: 0
          },
          indicator: {
            backgroundColor: zoneColors.accent,
            height: 2
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            border: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`,
            backgroundColor: isDark ? zoneColors.blackCard : zoneColors.white
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            padding: '6px 12px',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(186,35,46,0.05)' : 'rgba(186,35,46,0.03)'
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(186,35,46,0.1)' : 'rgba(186,35,46,0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(186,35,46,0.15)' : 'rgba(186,35,46,0.1)'
              }
            }
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? zoneColors.blackElevated : zoneColors.textDark,
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.625rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '4px 8px',
            border: `1px solid ${isDark ? zoneColors.borderMuted : 'transparent'}`
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.75rem'
          }
        }
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? zoneColors.borderDark : zoneColors.borderLight
          }
        }
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem'
          },
          displayedRows: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            textTransform: 'uppercase'
          },
          selectLabel: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            textTransform: 'uppercase'
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 600
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            border: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '12px 16px',
            borderBottom: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '16px'
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 16px',
            borderTop: `1px solid ${isDark ? zoneColors.borderDark : zoneColors.borderLight}`
          }
        }
      }
    }
  }), [mode, isDark]);

  // Set CSS custom properties on document root for components that need them
  useEffect(() => {
    const root = document.documentElement;
    
    // Core colors
    Object.entries(zoneColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });
    
    // Theme-aware colors (change based on light/dark mode)
    root.style.setProperty('--bg-page', isDark ? zoneColors.black : zoneColors.whitePage);
    root.style.setProperty('--bg-card', isDark ? zoneColors.blackCard : zoneColors.white);
    root.style.setProperty('--bg-elevated', isDark ? zoneColors.blackElevated : zoneColors.whiteMuted);
    root.style.setProperty('--text-primary', isDark ? zoneColors.textWhite : zoneColors.textDark);
    root.style.setProperty('--text-secondary', isDark ? zoneColors.textMuted : zoneColors.textDarkSecondary);
    root.style.setProperty('--border-color', isDark ? zoneColors.borderDark : zoneColors.borderLight);
  }, [isDark]);

  const value = {
    mode,
    toggleColorMode,
    colors: zoneColors,
    // Helper to get mode-aware colors
    getColor: (lightColor, darkColor) => isDark ? darkColor : lightColor
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
